import React from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import dayjs from "dayjs";

function ratingColor(r) {
  if (r >= 4.5) return "#00ff90";
  if (r >= 3.5) return "#60a5fa";
  if (r >= 2.5) return "#fbbf24";
  return "#f87171";
}

function TrendIcon({ t }) {
  if (t === "up")   return <TrendingUp   size={12} className="text-[#00ff90]" />;
  if (t === "down") return <TrendingDown size={12} className="text-red-400"   />;
  return <Minus size={12} className="text-white/20" />;
}

function reviewBadge(date) {
  if (!date) return <span className="text-white/20 text-xs">—</span>;
  const days = dayjs(date).diff(dayjs(), "day");
  if (days < 0)   return <span className="text-xs text-red-400/70">Overdue</span>;
  if (days <= 14) return <span className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 px-2 py-0.5 rounded-full">In {days}d</span>;
  if (days <= 30) return <span className="text-xs text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 px-2 py-0.5 rounded-full">In {days}d</span>;
  return <span className="text-xs text-white/30">{dayjs(date).format("DD MMM YYYY")}</span>;
}

export default function TeamPerformanceGrid({ loading, rows, onRowClick }) {
  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-12 rounded-lg bg-white/5 animate-pulse" />)}
      </div>
    );
  }

  if (!rows?.length) {
    return <p className="text-white/30 text-sm py-4 text-center italic">No performance data for this period.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/5">
            {["Member","Rating","Goals","Status","Last Review","Next Review",""].map(h => (
              <th key={h} className="px-3 py-3 text-left text-xs text-[#00ff90]/40 uppercase tracking-wider font-medium">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const color = ratingColor(r.rating);
            const isAtRisk = r.status === "AT_RISK";
            const goalsPct = r.goals?.total > 0 ? Math.round((r.goals.done / r.goals.total) * 100) : 0;
            return (
              <tr key={r.id} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                <td className="px-3 py-3">
                  <button onClick={() => onRowClick(r)} className="text-left hover:text-[#00ff90] transition-colors">
                    <p className="text-white font-medium">{r.name}</p>
                    <p className="text-white/30 text-xs">{r.role}</p>
                  </button>
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-bold" style={{ color }}>{r.rating?.toFixed(1)}</span>
                    <TrendIcon t={r.trend} />
                  </div>
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    <span className="text-white/50 text-xs">{r.goals?.done}/{r.goals?.total}</span>
                    <div className="h-1.5 w-16 rounded-full bg-white/5">
                      <div className="h-full rounded-full bg-[#00ff90]" style={{ width: `${goalsPct}%` }} />
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${
                    isAtRisk
                      ? "text-red-400 bg-red-500/10 border-red-500/20"
                      : "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20"
                  }`}>
                    {isAtRisk ? "At Risk" : "On Track"}
                  </span>
                </td>
                <td className="px-3 py-3 text-white/30 text-xs">{r.lastReview ? dayjs(r.lastReview).format("DD MMM YYYY") : "—"}</td>
                <td className="px-3 py-3">{reviewBadge(r.nextReview)}</td>
                <td className="px-3 py-3">
                  <button onClick={() => onRowClick(r)}
                    className="text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/60 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
                    Open
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}